import React, { useContext, useState } from "react";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import EmailIcon from "@mui/icons-material/Email";
import InputField from "@/components/Frontend/Inputs/InputField";
import TextAreaField from "@/components/Frontend/Inputs/TextArea";
import Button from "@/components/Frontend/CTA/Button";
import { URLS } from "@/utils/urls";
import { showToastSuccess, showToastError } from "@/utils/toastFunctions";
import AuthContext from "@/context/AuthContext";

const ContactUs = () => {
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [textArea, setTextArea] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useContext(AuthContext);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = {
            user: user?.user_id,
            first_name: firstName,
            last_name: lastName,
            email_address: email,
            message: textArea,
        };

        try {
            setIsLoading(true);
            const response = await fetch(URLS?.contactUs, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                showToastSuccess("Message sent successfully!");
                setFirstName("");
                setLastName("");
                setEmail("");
                setTextArea("");
            } else {
                showToastError("Failed to send message");
            }
        } catch {
            showToastError("Failed to send message");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="py-12 px-6 sm:px-4 mx-auto max-w-6xl">
            <div className="grid grid-cols-2 sm:grid-cols-1 gap-12">
                {/* Contact Form */}
                <div className="border p-8 shadow-lg rounded-lg bg-white">
                    <h2 className="text-2xl font-semibold text-[#202b3a] mb-6">
                        Get in Touch
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="firstName" className="block text-gray-700 font-medium">
                                First Name*
                            </label>
                            <InputField
                                name="firstName"
                                type="text"
                                id="firstName"
                                value={firstName}
                                required
                                autoComplete="true"
                                onChange={(e) => setFirstName(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="lastName" className="block text-gray-700 font-medium">
                                Last Name*
                            </label>
                            <InputField
                                name="lastName"
                                type="text"
                                id="lastName"
                                value={lastName}
                                required
                                autoComplete="true"
                                onChange={(e) => setLastName(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-gray-700 font-medium">
                                Email*
                            </label>
                            <InputField
                                type="email"
                                name="email"
                                id="email"
                                value={email}
                                required
                                autoComplete="true"
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="message" className="block text-gray-700 font-medium">
                                Comments/Questions*
                            </label>
                            <TextAreaField
                                id="message"
                                name="message"
                                rows={6}
                                value={textArea}
                                required
                                onChange={(e) => setTextArea(e.target.value)}
                                maxLength={1000}
                            />
                        </div>
                        <Button
                            type="submit"
                            text={isLoading ? "Submitting..." : "Submit"}
                            disabled={isLoading}
                            customClassName="text-lg text-white font-medium bg-[#E25822] hover:bg-[#c9461f] transition-all rounded-lg w-full py-3"
                        />
                    </form>
                </div>

                {/* Contact Details & Map */}
                <div className="space-y-6">
                    <div className="w-full">
                        <iframe
                            width="100%"
                            height="350px"
                            className="rounded-lg shadow-lg"
                            loading="lazy"
                            allowFullScreen
                            referrerPolicy="no-referrer-when-downgrade"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4386.896053676317!2d76.80378908935305!3d30.65867420717624!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390feb365ab1fd83%3A0x4bd8ef389a18e7cc!2sZirakpur%2C%20Punjab%2C%20India!5e0!3m2!1sen!2sin!4v1707253746104"
                        ></iframe>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                        <h2 className="text-xl font-semibold text-[#202b3a] mb-4">
                            Contact Information
                        </h2>
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                                <LocalPhoneIcon className="text-[#E25822]" />
                                <span className="text-gray-700 font-medium">Call Us:</span>
                                <a
                                    href="tel:+918527237589"
                                    className="text-[#777777] hover:text-[#E25822] transition-all"
                                >
                                    +91 85272 37589
                                </a>
                            </div>
                            <div className="flex items-center space-x-3">
                                <EmailIcon className="text-[#E25822]" />
                                <span className="text-gray-700 font-medium">Email:</span>
                                <a
                                    href="mailto:sales@hotbuymart.com"
                                    className="text-[#777777] hover:text-[#E25822] transition-all"
                                >
                                    sales@hotbuymart.com
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactUs;
